import { useCallback, useEffect, useMemo, useState } from 'react';
import { DialplanService } from '../../../services/dialplan.service';
import { useSession } from '../../../hooks/use-session.hook';
import { usePermissions } from '../../../hooks/use-permissions.hook';
import { PROFILES } from '../../../constants';
import { AlertService } from '../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../hooks/use-request.hook';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Icon from '../../../components/forms/icon.component';
import Aside from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import FilterBar from '../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../components/cards/header-stats.component';
import CampaignEffectivenessTable from './table-dialplan';
import OpensipsEntityModal from '../opensips/opensips-entity.modal';
import OpensipsReloadButton from '../opensips/opensips-reload.button';

const Dialplan: React.FC = () => {
  useBgPages();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const { isAdmin, can } = usePermissions();
  const canWrite = can('opensips.write');
  const canReload = can('opensips.reload');
  const isRoot = session?.profile === PROFILES.SUPER_ADMIN;

  const [routesRaw, setRoutesRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [showModal, setShowModal] = useState<{ show: boolean; data: any; duplicate: boolean }>({
    show: false,
    data: null,
    duplicate: false
  });

  const dialplanRows = useMemo<any[]>(() => {
    if (!Array.isArray(routesRaw)) return [];
    return routesRaw.map((item: any) => ({
      id: item.id,
      dpid: item.dpid ?? 0,
      pr: item.pr ?? 0,
      matchOp: item.matchOp || 'regex',
      matchExp: item.matchExp || '—',
      matchFlags: item.matchFlags ?? 0,
      substExp: item.substExp || '—',
      replExp: item.replExp || '—',
      timerec: item.timerec || '—',
      enabled: item.enabled ?? false,
      attrs: item.attrs && item.attrs !== '' ? item.attrs : '—',
      description: item.description || 'Sin descripción',
      company: item.dpid ? String(item.dpid) : '0',
      raw: item
    }));
  }, [routesRaw]);

  const dpIdOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return dialplanRows.reduce<FilterOption[]>((acc, r) => {
      const idStr = String(r.dpid);
      if (idStr && !seen.has(idStr)) {
        seen.add(idStr);
        acc.push({ value: idStr, label: `DPID: ${idStr}` });
      }
      return acc;
    }, []);
  }, [dialplanRows]);

  const statusOptions = useMemo<FilterOption[]>(() => {
    return [
      { value: 'true', label: 'ACTIVO' },
      { value: 'false', label: 'INACTIVO' }
    ];
  }, []);

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];

    if (dpIdOptions.length > 0) {
      defs.push({
        key: 'company',
        label: 'Dialplan ID',
        icon: <Icon icon="ki-sliders" size="fs-7" />,
        options: dpIdOptions,
        multi: false,
        searchable: true
      });
    }

    defs.push({
      key: 'status',
      label: 'Estado',
      icon: <Icon icon="ki-shield-search" size="fs-7" />,
      options: statusOptions,
      multi: false,
      searchable: false
    });

    return defs;
  }, [dpIdOptions, statusOptions]);

  const displayRows = useMemo(() => {
    let rows = dialplanRows;

    if (filters.company) {
      rows = rows.filter((r) => String(r.dpid) === filters.company);
    }

    if (filters.status) {
      const targetStatus = filters.status === 'true';
      rows = rows.filter((r) => r.enabled === targetStatus);
    }

    return [...rows].sort((a, b) => a.id - b.id);
  }, [dialplanRows, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => {
    setFilters(next);
  }, []);

  const fetchDialplan = useCallback(async () => {
    try {
      const response = await call(() => DialplanService.getDialplan());
      setRoutesRaw(response);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteDialplan = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = DialplanService.deleteDialplan(String(id));

        await AlertService.toastLoading(
          deletePromise,
          'Eliminando registro...',
          'Registro eliminado exitosamente.',
          'Ocurrió un error al intentar eliminar el registro.'
        );
        fetchDialplan();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchDialplan]
  );

  const handleCloseModal = useCallback(
    async (refresh: boolean) => {
      if (refresh) await fetchDialplan();
      setShowModal({ show: false, data: null, duplicate: false });
    },
    [fetchDialplan]
  );

  /** Habilitar/deshabilitar la regla sin abrir el modal: PUT con la fila completa (dispara dp_reload). */
  const handleToggleEnabled = useCallback(
    async (row: any) => {
      const it = row.raw;
      const next = !it.enabled;
      const ok = await AlertService.question(
        next ? 'Habilitar regla' : 'Deshabilitar regla',
        `Regla #${it.id} (dpid ${it.dpid}, pr ${it.pr}) quedará ${next ? 'HABILITADA' : 'DESHABILITADA'} y se ejecutará dp_reload.`,
        () => Promise.resolve(true),
        'Continuar'
      );
      if (!ok) return;
      try {
        const p = DialplanService.update(it.id, {
          dpid: it.dpid,
          pr: it.pr,
          matchOp: it.matchOp,
          matchExp: it.matchExp ?? '',
          matchFlags: it.matchFlags ?? 0,
          substExp: it.substExp ?? null,
          replExp: it.replExp ?? null,
          timerec: it.timerec ?? null,
          enabled: next,
          attrs: it.attrs ?? null,
          description: it.description ?? null
        });
        await AlertService.toastLoading(
          p,
          'Actualizando...',
          'Regla actualizada y dp_reload ejecutado.',
          'No se pudo actualizar la regla.'
        );
        fetchDialplan();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchDialplan]
  );

  const handleRefresh = useCallback(async () => {
    await fetchDialplan();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [fetchDialplan]);

  useEffect(() => {
    fetchDialplan();
  }, [fetchDialplan]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: 'Reglas Totales',
                    value: displayRows.length,
                    icon: 'ki-duotone ki-menu fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: 'Reglas Activas',
                    value: displayRows.filter((r) => r.enabled).length,
                    icon: 'ki-duotone ki-check-circle fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  <div className="d-flex flex-wrap gap-2">
                    {canReload && (
                      <OpensipsReloadButton
                        label="Recargar dialplan"
                        command="dp_reload"
                        onReload={DialplanService.reload}
                      />
                    )}
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                        onClick={() => setShowModal({ show: true, data: null, duplicate: false })}
                      >
                        <Icon icon="ki-plus" size="fs-3" />
                        Agregar regla
                      </button>
                    )}
                  </div>
                }
              />
              {dialplanRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={[]}
                  selectedChannel=""
                  onChannelChange={() => {}}
                />
              )}
              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <CampaignEffectivenessTable
                    rows={displayRows}
                    isLoading={isRequesting}
                    isRoot={isRoot}
                    isAdmin={isAdmin}
                    canWrite={canWrite}
                    onEdit={(row: any) =>
                      setShowModal({ show: true, data: row.raw, duplicate: false })
                    }
                    onDuplicate={(row: any) =>
                      setShowModal({ show: true, data: row.raw, duplicate: true })
                    }
                    onToggleEnabled={handleToggleEnabled}
                    onDelete={handleDeleteDialplan}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showModal.show && (
        <OpensipsEntityModal
          kind="dialplan"
          show={showModal.show}
          payload={showModal.data}
          duplicate={showModal.duplicate}
          onRequestClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Dialplan;
